import React, { useState } from 'react';
import { FolderPlus, Loader2, Box } from 'lucide-react';
import { useCollectionStore } from '../../store/collectionStore';
import { useAuthStore } from '../../store/authStore';

const NoCollectionView: React.FC = () => {
  const { createBlankCollection } = useCollectionStore();
  const { signOut, user, profile } = useAuthStore();

  const [name, setName] = useState('');
  const [customer, setCustomer] = useState('');
  const [address, setAddress] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayName = profile?.displayName ?? user?.email?.split('@')[0] ?? 'there';

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    setIsSaving(true);
    setError(null);
    try {
      await createBlankCollection(trimmedName, customer.trim(), address.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create collection. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#171a1f] px-4">
      <div className="flex items-center gap-2 mb-10">
        <Box size={28} className="text-blue-400" />
        <span className="text-2xl font-bold text-blue-400">NetBuilder</span>
      </div>

      <div className="w-full max-w-md bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-700">
          <h1 className="text-lg font-semibold text-white">Welcome, {displayName}!</h1>
          <p className="text-sm text-slate-400 mt-1">
            Create your first collection to get started. A collection holds all your
            designs, sites, floorplans, and inventory.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Collection name <span className="text-red-400">*</span>
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              placeholder="e.g. Acme Corp Network"
              className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Customer name</label>
              <input
                value={customer}
                onChange={e => setCustomer(e.target.value)}
                placeholder="Customer"
                className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Address</label>
              <input
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="Address"
                className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded px-3 py-2">{error}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between gap-3">
          <button
            onClick={() => signOut()}
            className="text-xs text-slate-400 hover:text-slate-300 transition-colors"
          >
            Sign out
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || isSaving}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {isSaving ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <FolderPlus size={15} />
            )}
            {isSaving ? 'Creating…' : 'Create collection'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoCollectionView;
